import clsx from 'clsx';
import type React from 'react';
import type { Ref } from 'react';
import { Fragment, forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import type { Observable } from 'rxjs';
import useTestTag from '../../common/hooks/use-test-tag';
import Button from '../button/button';
import type { ModalDialogButtonSetting } from '../common-interfaces';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';

interface DialogResult {
  buttonName: string | undefined;
}

export interface DialogRef {
  show: () => void;
  close: () => void;
  subscribeToDialogResult?: () => Observable<DialogResult> | undefined;
}

export interface DialogProps extends React.PropsWithChildren {
  buttons?: Array<ModalDialogButtonSetting>;
  buttonType?: 'button' | 'submit';
  className?: string;
  containerClassName?: string;
  contentClassName?: string;
  footer?: string | React.JSX.Element;
  fullScreen?: boolean;
  header?: string | React.JSX.Element;
  id?: string;
  loading?: boolean;
  showCloseIcon?: boolean;
  visible?: boolean;
  onClose?: (returnValue?: string) => void;
  /**
   * Controls whether to use the native HTML `<dialog>` element or a custom portal-based implementation.
   *
   * @default true
   *
   * **When to use `true` (native dialog - default):**
   * - Standard modal dialogs with typical behavior
   * - Better accessibility (native focus trap, ESC key handling)
   * - Better performance (no React portal overhead)
   * - Built-in backdrop click handling
   *
   * **When to use `false` (custom portal implementation):**
   * - **When wrapping Dialog with OdinTransition or Framer Motion** - Native `<dialog>` elements conflict with
   *   Framer Motion's animation requirements because they render in the browser's top layer, preventing proper
   *   animation control. Use `useNativeDialog={false}` to enable smooth enter/exit transitions.
   * - When you need more control over the backdrop or transition behavior
   * - When compatibility with older browsers that don't support `<dialog>` is required
   * - When the native dialog constraints (e.g., top layer stacking) cause other integration issues
   *
   * **Key differences:**
   * - Native: Uses `<dialog>` element with `showModal()`, renders in browser's top layer
   * - Custom: Uses React portal with manual backdrop and positioning, renders in document.body
   */
  useNativeDialog?: boolean;
  closeOnClickOutside?: boolean;
}

function Dialog(
  {
    buttons,
    buttonType = 'button',
    children,
    className,
    containerClassName,
    contentClassName,
    footer,
    fullScreen = false,
    header,
    id,
    loading = false,
    showCloseIcon = true,
    visible = false,
    onClose,
    useNativeDialog = true,
    closeOnClickOutside = true,
  }: DialogProps,
  ref: Ref<DialogRef>
) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const tagCreator = useTestTag('modal');
  const onCloseCalledRef = useRef(false);

  // Sync native dialog open state with visible prop
  useLayoutEffect(() => {
    if (!useNativeDialog) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Only make DOM calls when current state doesn't match target state.
    // This prevents race conditions from rapid visible prop changes because
    // we check the actual dialog.open state right before each DOM call.
    if (visible && !dialog.open) {
      dialog.showModal();
      // Reset the flag only during open transition (closed → open).
      // This allows onClose to be called when the dialog actually closes.
      onCloseCalledRef.current = false;
    } else if (!visible && dialog.open) {
      dialog.close();
    }
  }, [visible, useNativeDialog]);

  const show = () => {
    if (!useNativeDialog) {
      return;
    }
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  };

  const close = (returnValue?: string) => {
    if (!useNativeDialog) {
      onClose?.(returnValue);
      return;
    }

    const dialog = dialogRef.current;
    if (dialog?.open) {
      // Set returnValue before calling close() to ensure it's captured correctly
      if (returnValue) {
        dialog.returnValue = returnValue;
      }
      dialog.close();
      // Call onClose immediately to ensure parent state is updated without waiting for the native 'close' event.
      // Set the flag AFTER calling onClose to prevent handleNativeClose from calling it again.
      // This handles cases where dialog.close() triggers the native 'close' event synchronously.
      onClose?.(returnValue);
      onCloseCalledRef.current = true;
    } else {
      // If already closed, just notify parent (only if not already called)
      if (!onCloseCalledRef.current) {
        onCloseCalledRef.current = true;
        onClose?.(returnValue);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    show,
    close,
  }));

  const onDialogClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (closeOnClickOutside && event.target === dialogRef.current) {
      close('overlay');
    }
  };

  const handleNativeClose = useCallback(() => {
    // This fires when the dialog is closed by ANY means (ESC, form method="dialog", dialog.close())
    // Only call onClose if it hasn't been called already (e.g., from the close() method)
    // This prevents duplicate calls that could trigger side effects twice.
    if (!onCloseCalledRef.current) {
      onCloseCalledRef.current = true;
      const returnValue = dialogRef.current?.returnValue;
      onClose?.(returnValue);
    }
  }, [onClose]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: close and tagCreator are stable functions
  const renderHeader = useCallback(() => {
    return (
      <div className="flex shrink-0 items-center gap-2 pb-2">
        <div className="grow truncate text-base font-medium">{header}</div>
        {showCloseIcon && (
          <div
            className="grid h-full place-content-start cursor-pointer text-primary-2"
            onClick={() => close('close-icon')}
            ref={tagCreator('close-icon')}
          >
            <OdinIcon icon="X" />
          </div>
        )}
      </div>
    );
  }, [header, showCloseIcon]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: close and tagCreator are stable functions
  const renderFooterButton = useCallback(
    (button: ModalDialogButtonSetting) => (
      <div className="flex-auto" ref={tagCreator(`${button.text?.replace(/\s+/g, '-').toLowerCase()}-button`)}>
        <Button
          htmlType={buttonType}
          classNames={button.classNames}
          type={button.type}
          variant={button.variant}
          form={button.form}
          disabled={button.disabled}
          name={button.name}
          onClick={() => {
            button.onClick?.();
            if (button.closeModalOnClick) {
              close(button.name);
            }
          }}
        >
          {button.text}
        </Button>
      </div>
    ),
    [buttonType]
  );

  const renderFooter = useCallback(() => {
    if (buttons || footer) {
      let renderedButtons: React.JSX.Element = <></>;
      let leftAlignedButtons: ModalDialogButtonSetting[] = [];

      if (buttons) {
        leftAlignedButtons = buttons.filter((button) => button.alignment === 'left');
        const rightAlignedButtons = buttons.filter((button) => button.alignment !== 'left');

        const buttonsWrapperClasses = clsx('flex items-center justify-between', {
          'w-full': leftAlignedButtons.length > 0 || !footer,
        });

        renderedButtons = (
          <div className={buttonsWrapperClasses}>
            <div className="flex items-center justify-center gap-x-2 self-start">
              {leftAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
            <div className="flex items-center justify-center gap-x-2 self-end">
              {rightAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
          </div>
        );
      }

      const footerClasses = clsx(
        'truncate p-1',
        { grow: leftAlignedButtons.length > 0 },
        { 'flex-1': leftAlignedButtons.length === 0 }
      );

      return (
        <div className="flex shrink-0 border-t border-solid border-primary-5 pt-4">
          {footer && <div className={footerClasses}>{footer}</div>}
          {renderedButtons}
        </div>
      );
    }
  }, [buttons, footer, renderFooterButton]);

  const containerStyles = clsx(
    'flex flex-col p-4 max-h-[calc(100svh-2rem)]',
    {
      'w-[calc(100svw-2rem)] h-[calc(100svh-2rem)]': fullScreen,
    },
    containerClassName
  );

  const contentStyles = clsx('flex-1 overflow-auto min-h-0 py-4 custom-scroll', contentClassName);

  const baseDialogStyles = 'm-auto rounded-md outline-hidden overflow-visible text-gray-1';

  const dialogStyles = clsx(
    baseDialogStyles,
    {
      '[&:not([open])]:pointer-events-none': useNativeDialog, // Only applies to actual <dialog> elements
    },
    className
  );

  if (!useNativeDialog) {
    if (!visible) {
      return null;
    }

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[1000]">
        <div className="absolute inset-0 bg-[rgba(0,0,0,0.5)] z-0" onClick={() => close('overlay')} />
        <div className="relative flex h-full w-full items-center justify-center z-10">
          {loading && (
            <div className="absolute inset-0 z-20 bg-[rgba(247,247,247,0.4)] grid place-content-center">
              <OdinIcon icon="Loader" size={OdinIconSize.Medium} spin />
            </div>
          )}
          <div className={clsx(baseDialogStyles, 'relative z-10')} role="dialog" aria-modal="true" id={id}>
            <div className={containerStyles} ref={tagCreator('container')}>
              {renderHeader()}
              <div className={contentStyles} ref={tagCreator('content')}>
                {children}
              </div>
              {renderFooter()}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const createDialog = () => (
    <dialog ref={dialogRef} id={id} className={dialogStyles} onClick={onDialogClick} onClose={handleNativeClose}>
      {loading && (
        <div className="absolute w-full h-full z-10 bg-[rgba(247,247,247,0.4)] grid place-content-center">
          <OdinIcon icon="Loader" size={OdinIconSize.Medium} spin />
        </div>
      )}
      <div className={containerStyles} ref={tagCreator('container')}>
        {renderHeader()}
        <div className={contentStyles} ref={tagCreator('content')}>
          {children}
        </div>
        {renderFooter()}
      </div>
    </dialog>
  );

  return ReactDOM.createPortal(createDialog(), document.body);
}

export default forwardRef(Dialog);
